import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";

const renderStars = (rating) => {
  const safeRating = Math.max(0, Math.min(5, Number(rating) || 0));
  return "★".repeat(safeRating) + "☆".repeat(5 - safeRating);
};

export const StoreReviewsSection = () => {
  const { user, listStoreReviews, submitStoreReview } = useStore();
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const loadReviews = async () => {
    setLoading(true);
    try {
      const response = await listStoreReviews(12);
      setReviews(response.items || []);
      setAverageRating(Number(response.average_rating) || 0);
      setTotal(Number(response.total) || 0);
    } catch {
      setReviews([]);
      setAverageRating(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      toast.error("Please sign in from Account to submit a review.");
      return;
    }

    if (comment.trim().length < 5) {
      toast.error("Please write at least 5 characters.");
      return;
    }

    setSubmitting(true);
    try {
      await submitStoreReview({ rating, comment: comment.trim() });
      toast.success("Thank you for your review.");
      setComment("");
      setRating(5);
      await loadReviews();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not submit review right now");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mx-auto mt-20 w-full max-w-[1240px] px-4 sm:px-6 lg:px-8" data-testid="home-store-reviews-section">
      <div className="rounded-3xl border border-white/10 bg-[#121212] p-6 sm:p-8">
        <p className="text-xs uppercase tracking-[0.22em] text-[#C2A45D]" data-testid="home-store-reviews-kicker">
          COMMUNITY REVIEWS
        </p>
        <h2 className="mt-3 text-3xl text-[#f4ebdd] sm:text-4xl" data-testid="home-store-reviews-heading">
          What our customers feel
        </h2>
        <p className="mt-2 text-sm text-[#b7a88b]" data-testid="home-store-reviews-summary">
          {total > 0 ? `${averageRating.toFixed(1)} / 5 average from ${total} review${total > 1 ? "s" : ""}` : "Be the first to review Eternal Order."}
        </p>

        <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr,1fr]">
          <div className="space-y-3" data-testid="home-store-reviews-list">
            {loading ? (
              <p className="rounded-xl border border-white/10 px-4 py-3 text-sm text-[#b7a88b]" data-testid="home-store-reviews-loading">
                Loading reviews...
              </p>
            ) : reviews.length === 0 ? (
              <p className="rounded-xl border border-white/10 px-4 py-3 text-sm text-[#b7a88b]" data-testid="home-store-reviews-empty">
                No reviews yet.
              </p>
            ) : (
              reviews.map((review) => (
                <article key={review.id} className="rounded-xl border border-white/10 bg-black/20 px-4 py-3" data-testid={`home-store-review-card-${review.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-[#f2e8d7]" data-testid={`home-store-review-user-${review.id}`}>
                      {review.user_name}
                    </p>
                    <p className="text-xs text-[#cdb67f]" data-testid={`home-store-review-rating-${review.id}`}>
                      {renderStars(review.rating)}
                    </p>
                  </div>
                  <p className="mt-2 text-sm text-[#c5b69b]" data-testid={`home-store-review-comment-${review.id}`}>
                    {review.comment}
                  </p>
                </article>
              ))
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid="home-store-review-form-card">
            <p className="text-xs uppercase tracking-[0.13em] text-[#C2A45D]" data-testid="home-store-review-form-heading">
              Share your review
            </p>

            {user ? (
              <form className="mt-3 space-y-3" onSubmit={onSubmit} data-testid="home-store-review-form">
                <label className="block text-xs text-[#beaf92]" data-testid="home-store-review-rating-label">
                  Rating
                  <select
                    value={rating}
                    onChange={(event) => setRating(Number(event.target.value) || 5)}
                    className="mt-1 w-full rounded-lg border border-white/20 bg-[#0f0f0f] px-3 py-2 text-sm text-[#f6ecdc]"
                    data-testid="home-store-review-rating-select"
                  >
                    {[5, 4, 3, 2, 1].map((value) => (
                      <option key={value} value={value}>
                        {value} Star{value > 1 ? "s" : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-xs text-[#beaf92]" data-testid="home-store-review-comment-label">
                  Comment
                  <textarea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    className="mt-1 h-24 w-full rounded-lg border border-white/20 bg-[#0f0f0f] px-3 py-2 text-sm text-[#f6ecdc]"
                    placeholder="Tell us about your Eternal Order experience"
                    data-testid="home-store-review-comment-input"
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-[#C2A45D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-black disabled:opacity-60"
                  data-testid="home-store-review-submit-button"
                >
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </form>
            ) : (
              <div className="mt-3 space-y-2" data-testid="home-store-review-login-required">
                <p className="text-sm text-[#b7a88b]">Sign in to share your review.</p>
                <Link
                  to="/account"
                  className="inline-flex rounded-full border border-[#C2A45D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#C2A45D]"
                  data-testid="home-store-review-login-link"
                >
                  Go to Account
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
